import React, { useState, useEffect } from "react";
import { FiTrash2, FiEye, FiCheck, FiX, FiSearch, FiFilter, FiCalendar, FiTag, FiUsers, FiImage } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useCampaigns, useDeleteCampaign } from "../../hooks/useCampaigns.ts";
import { useToggleCampaignStatus } from "../../hooks/useAdmin.ts";
import Pagination from "../../components/Pagination.tsx";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from "../../theme.ts";
import { showConfirmation } from "../../services/confirmation.ts";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnDef,
} from "@tanstack/react-table";

interface Campaign {
  id: string;
  banner: string;
  title: string;
  brand_name: string;
  type: string;
  deadline: string;
  is_enabled_by_admin: boolean;
}

// Local fallback component for broken images
const FallbackImage: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`${className} flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800`}>
    <FiImage className="text-3xl text-gray-400 dark:text-gray-500" />
  </div>
);

const AdminCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [search, setSearch] = useState("");
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filterType, setFilterType] = useState<string>("all");
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>({});
  const { data: campaignsData } = useCampaigns();
  const deleteCampaignMutation = useDeleteCampaign();
  const toggleCampaignStatusMutation = useToggleCampaignStatus();

  // Initialize campaigns state when data loads
  useEffect(() => {
    if (campaignsData) {
      // Handle both direct array and nested data structure
      const data = Array.isArray(campaignsData)
        ? campaignsData
        : (campaignsData as any)?.data || [];

      setCampaigns(data);
    }
  }, [campaignsData]);

  const handleDelete = async (id: string) => {
    try {
      await deleteCampaignMutation.mutateAsync(id);
      setCampaigns(campaigns.filter((campaign) => campaign.id !== id));
    } catch (err) {
      console.log(err);
    }
  };

  const handleStatusChange = async (campaignId: string, newStatus: boolean) => {
    setLoadingStates(prev => ({ ...prev, [campaignId]: true }));

    try {
      setCampaigns(prevCampaigns =>
        prevCampaigns.map(campaign =>
          campaign.id === campaignId
            ? { ...campaign, is_enabled_by_admin: newStatus }
            : campaign
        )
      );

      await toggleCampaignStatusMutation.mutateAsync({ id: campaignId, status: newStatus });

    } catch (err) {
      console.error("Error updating campaign status:", err);
      // Revert optimism if needed
      setCampaigns(prevCampaigns =>
        prevCampaigns.map(campaign =>
          campaign.id === campaignId
            ? { ...campaign, is_enabled_by_admin: !newStatus }
            : campaign
        )
      );
    } finally {
      setLoadingStates(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  const openStatusConfirmation = (campaignId: string, currentStatus: boolean, title: string) => {
    const newStatus = !currentStatus;
    showConfirmation(
      'Confirm Status Change',
      `Are you sure you want to ${newStatus ? "enable" : "disable"} the campaign "${title}"?`,
      newStatus ? 'Enable' : 'Disable',
      'Cancel',
      () => handleStatusChange(campaignId, newStatus)
    );
  };

  const openDeleteConfirmation = (campaignId: string, title: string) => {
    showConfirmation(
      'Delete Campaign',
      `Are you sure you want to delete the campaign "${title}"? This action cannot be undone.`,
      'Delete',
      'Cancel',
      () => handleDelete(campaignId)
    );
  };

  // Handle image error
  const handleImageError = (campaignId: string) => {
    setImageErrors(prev => ({ ...prev, [campaignId]: true }));
  };

  // Filter campaigns based on search and type
  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesSearch = campaign?.title?.toLowerCase().includes(search.toLowerCase()) ||
      campaign?.brand_name?.toLowerCase().includes(search.toLowerCase());
    
    const matchesType = filterType === "all" || campaign?.type?.toLowerCase() === filterType.toLowerCase();
    
    return matchesSearch && matchesType;
  });

  // Get unique campaign types for filter
  const campaignTypes = ["all", ...new Set(campaigns.map(c => c.type).filter(Boolean))];

  // Pagination calculations
  const totalPages = Math.ceil(filteredCampaigns.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  // TanStack Table column definitions
  const columnHelper = createColumnHelper<Campaign>();

  const columns: ColumnDef<Campaign, any>[] = [
    columnHelper.accessor('banner', {
      header: 'Banner',
      cell: (info) => {
        const campaignId = info.row.original.id;
        return (
          <div className="relative w-24 h-16">
            {imageErrors[campaignId] ? (
              <FallbackImage className="w-24 h-16 rounded-xl" />
            ) : (
              <img
                src={info.getValue()}
                alt={`${info.row.original.title} banner`}
                className="w-24 h-16 object-cover rounded-xl shadow-md transition-all duration-300 hover:shadow-xl hover:brightness-110"
                style={{ borderColor: currentTheme.border }}
                onError={() => handleImageError(campaignId)}
                loading="lazy"
              />
            )}
          </div>
        );
      },
      enableSorting: false,
    }),
    columnHelper.accessor('title', {
      header: 'Title',
      cell: (info) => (
        <div className="transition-all duration-300 hover:translate-x-1">
          <span className="font-semibold block" style={{ color: currentTheme.text }}>{info.getValue()}</span>
          <span className="text-xs font-mono" style={{ color: currentTheme.textSecondary }}>{info.row.original.id.substring(0, 8)}...</span>
        </div>
      ),
    }),
    columnHelper.accessor('brand_name', {
      header: 'Brand',
      cell: (info) => (
        <div className="flex items-center gap-2 transition-all duration-300 hover:translate-x-1">
          <FiUsers className="text-sm" style={{ color: currentTheme.primary }} />
          <span style={{ color: currentTheme.text }}>{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor('type', {
      header: 'Type',
      cell: (info) => (
        <span className="px-3 py-1 rounded-full text-xs font-medium transition-all duration-300 hover:shadow-md hover:scale-105 inline-block"
          style={{ 
            backgroundColor: `${currentTheme.primary}15`,
            color: currentTheme.primary,
            border: `1px solid ${currentTheme.primary}30`
          }}>
          {info.getValue()}
        </span>
      ),
    }),
    columnHelper.accessor('deadline', {
      header: 'Deadline',
      cell: (info) => {
        const date = new Date(info.getValue());
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const isOverdue = date < today;
        const isToday = date.toDateString() === today.toDateString();
        
        let statusColor = currentTheme.text;
        if (isOverdue) statusColor = currentTheme.error;
        if (isToday) statusColor = currentTheme.warning;
        
        return (
          <div className="flex items-center gap-2 transition-all duration-300 hover:translate-x-1">
            <FiCalendar className={isOverdue ? 'text-red-500' : isToday ? 'text-yellow-500' : 'text-green-500'} size={14} />
            <span style={{ color: statusColor }}>
              {date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              })}
            </span>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'status',
      header: 'Status',
      cell: (props) => {
        const campaign = props.row.original;
        return (
          <button
            onClick={() => openStatusConfirmation(campaign.id, campaign.is_enabled_by_admin, campaign.title)}
            disabled={loadingStates[campaign.id]}
            className="relative group/status focus:outline-none"
          >
            <div className={`flex items-center justify-center w-28 py-2 rounded-xl font-medium text-sm transition-all duration-300 
              ${loadingStates[campaign.id] ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-lg cursor-pointer'}`}
              style={{ 
                background: campaign.is_enabled_by_admin 
                  ? `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.primary}DD)` 
                  : `linear-gradient(135deg, ${currentTheme.error}, ${currentTheme.error}DD)`,
                color: currentTheme.inverseText,
                boxShadow: `0 4px 12px ${campaign.is_enabled_by_admin ? currentTheme.primary + '40' : currentTheme.error + '40'}`
              }}>
              {loadingStates[campaign.id] ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>...</span>
                </div>
              ) : (
                <>
                  {campaign.is_enabled_by_admin ? <FiCheck className="mr-2" size={16} /> : <FiX className="mr-2" size={16} />}
                  {campaign.is_enabled_by_admin ? 'Enabled' : 'Disabled'}
                </>
              )}
            </div>
          </button>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (props) => {
        const campaign = props.row.original;
        return (
          <div className="flex gap-2">
            <button
              onClick={() => openDeleteConfirmation(campaign.id, campaign.title)}
              className="p-2.5 rounded-xl transition-all duration-300 hover:shadow-lg group/delete focus:outline-none"
              style={{ 
                color: currentTheme.error,
                backgroundColor: `${currentTheme.error}10`,
              }}
              title="Delete campaign"
            >
              <FiTrash2 size={18} className="group-hover/delete:rotate-12 transition-transform" />
            </button>
            <button
              onClick={() => navigate(`/dashboard/campaings/${campaign.id}`)}
              className="p-2.5 rounded-xl transition-all duration-300 hover:shadow-lg group/view focus:outline-none"
              style={{ 
                color: currentTheme.primary,
                backgroundColor: `${currentTheme.primary}10`,
              }}
              title="View details"
            >
              <FiEye size={18} className="group-hover/view:scale-110 transition-transform" />
            </button>
          </div>
        );
      },
    }),
  ];

  // TanStack Table instance
  const table = useReactTable({
    data: filteredCampaigns,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: totalPages,
  });

  // Get sorted rows, then paginate manually
  const sortedRows = table.getRowModel().rows;
  const paginatedRows = sortedRows.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen p-4 md:p-6 overflow-x-hidden" style={{ backgroundColor: currentTheme.background }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Campaign Management
          </h1>
          <p className="text-sm" style={{ color: currentTheme.textSecondary }}>
            Manage and monitor all marketing campaigns
          </p>
        </div>

        {/* Filters Section */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: currentTheme.textSecondary }} />
            <input
              type="text"
              placeholder="Search campaigns by title or brand..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl focus:outline-none transition-all duration-300 focus:ring-2"
              style={{ 
                backgroundColor: currentTheme.surface, 
                color: currentTheme.text, 
                border: `1px solid ${currentTheme.border}`,
                boxShadow: `0 4px 12px ${currentTheme.primary}10`
              }}
            />
          </div>
          
          <div className="relative min-w-[200px]">
            <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: currentTheme.textSecondary }} />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl focus:outline-none transition-all duration-300 appearance-none cursor-pointer"
              style={{ 
                backgroundColor: currentTheme.surface, 
                color: currentTheme.text, 
                border: `1px solid ${currentTheme.border}`,
                boxShadow: `0 4px 12px ${currentTheme.primary}10`
              }}
            >
              {campaignTypes.map(type => (
                <option key={type} value={type} className="capitalize">
                  {type === 'all' ? 'All Types' : type}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Campaign Stats */}
        {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard 
            title="Total Campaigns"
            value={campaigns.length}
            icon={<FiTag />}
            color="from-blue-500 to-blue-600"
            theme={currentTheme}
          />
          <StatCard 
            title="Enabled"
            value={campaigns.filter(c => c.is_enabled_by_admin).length}
            icon={<FiCheck />}
            color="from-green-500 to-green-600"
            theme={currentTheme}
          />
          <StatCard 
            title="Disabled"
            value={campaigns.filter(c => !c.is_enabled_by_admin).length}
            icon={<FiX />}
            color="from-red-500 to-red-600"
            theme={currentTheme}
          />
          <StatCard 
            title="Active Types"
            value={campaignTypes.length - 1}
            icon={<FiFilter />}
            color="from-purple-500 to-purple-600"
            theme={currentTheme}
          />
        </div> */}

        {/* Campaigns Table */}
        <div className="rounded-2xl shadow-2xl overflow-hidden transition-all hover:shadow-3xl"
             style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
          <div className="overflow-x-auto overflow-y-hidden">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id} style={{ background: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.primary}DD)` }}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                        className={`px-4 py-4 text-left font-semibold text-sm ${header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-white/10' : ''}`}
                        style={{ color: currentTheme.inverseText }}
                      >
                        <div className="flex items-center gap-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className="flex flex-col text-[10px] leading-none">
                              <span style={{ opacity: header.column.getIsSorted() === 'asc' ? 1 : 0.5 }}>▲</span>
                              <span style={{ opacity: header.column.getIsSorted() === 'desc' ? 1 : 0.5 }}>▼</span>
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {paginatedRows.length > 0 ? (
                  paginatedRows.map((row, index) => (
                    <tr
                      key={row.id}
                      className="group transition-all duration-300 hover:bg-opacity-80"
                      style={{ 
                        backgroundColor: index % 2 === 0 ? currentTheme.surface : currentTheme.card,
                        borderBottom: `1px solid ${currentTheme.border}`
                      }}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-4 py-3" style={{ color: currentTheme.text }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="p-12 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 flex items-center justify-center">
                          <FiSearch className="text-3xl" style={{ color: currentTheme.primary }} />
                        </div>
                        <h3 className="text-xl font-semibold" style={{ color: currentTheme.text }}>
                          {campaigns.length === 0 ? "No Campaigns Found" : "No Matching Results"}
                        </h3>
                        <p style={{ color: currentTheme.textSecondary }}>
                          {campaigns.length === 0 
                            ? "There are no campaigns to display at the moment." 
                            : "Try adjusting your search or filter to find what you're looking for."}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredCampaigns.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-t" style={{ borderColor: currentTheme.border }}>
              <div className="flex items-center gap-4">
                <select
                  value={rowsPerPage}
                  onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                  className="px-3 py-2 rounded-xl border focus:outline-none focus:ring-2 transition-all cursor-pointer"
                  style={{
                    backgroundColor: currentTheme.surface,
                    color: currentTheme.text,
                    borderColor: currentTheme.border
                  }}
                >
                  {[12, 25, 50, 100].map(value => (
                    <option key={value} value={value}>{value} per page</option>
                  ))}
                </select>
                <span className="text-sm" style={{ color: currentTheme.textSecondary }}>
                  Showing <span className="font-semibold" style={{ color: currentTheme.text }}>{startIndex + 1}</span> to{' '}
                  <span className="font-semibold" style={{ color: currentTheme.text }}>{Math.min(endIndex, filteredCampaigns.length)}</span> of{' '}
                  <span className="font-semibold" style={{ color: currentTheme.text }}>{filteredCampaigns.length}</span> campaigns
                </span>
              </div>
              <Pagination 
                currentPage={currentPage} 
                totalPages={totalPages} 
                onPageChange={handlePageChange} 
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  theme: any;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, theme }) => (
  <div className="group/stat relative overflow-hidden rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl"
       style={{ 
         background: `linear-gradient(135deg, ${theme.surface}, ${theme.card})`,
         border: `1px solid ${theme.border}`
       }}>
    <div className={`absolute inset-0 bg-gradient-to-r ${color} opacity-0 group-hover/stat:opacity-5 transition-opacity duration-500`}></div>
    <div className="relative">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-3 rounded-xl bg-gradient-to-r ${color} bg-opacity-10`}>
          <div className="text-white">{icon}</div>
        </div>
        <span className="text-3xl font-bold" style={{ color: theme.text }}>{value}</span>
      </div>
      <p className="text-sm" style={{ color: theme.textSecondary }}>{title}</p>
    </div>
  </div>
);

export default AdminCampaigns;